import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import {
  LifeBuoy, ArrowLeft, Send, CheckCircle2, Loader2, MessageCircle, Shield,
  Clock, Lock,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * TicketView — public support-ticket detail page.
 *
 * Route:  /support/ticket/:id?token=<view_token>
 *
 * The URL itself is the credential — the token was issued when the user
 * opened the ticket and is stored in the site's localStorage. Anyone who
 * possesses the URL can view + reply on the ticket. Admins reach the same
 * ticket via the /admin/support dashboard.
 */

const STATUS_META = {
  open:     { label: "Open",     color: "cyan",   dot: "var(--sc-cyan)"   },
  pending:  { label: "Awaiting You", color: "orange", dot: "var(--sc-orange)" },
  resolved: { label: "Resolved", color: "volt",   dot: "var(--sc-volt)"   },
  closed:   { label: "Closed",   color: "pink",   dot: "#888"             },
};

export default function TicketView() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);

  const load = () => {
    if (!id || !token) {
      setErr("Missing ticket ID or token.");
      setLoading(false);
      return;
    }
    setLoading(true);
    api.get(`/support/tickets/${id}`, { params: { token } })
      .then((r) => { setTicket(r.data); setErr(""); })
      .catch((e) => setErr(e?.response?.data?.detail || "Could not load ticket."))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [id, token]);

  const sendReply = async (e) => {
    e.preventDefault();
    if (!reply.trim() || sending) return;
    setSending(true);
    try {
      await api.post(`/support/tickets/${id}/reply`, { message: reply.trim() }, { params: { token } });
      setReply("");
      toast.success("Reply sent.");
      load();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Could not send reply.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0C] relative">
      <div className="neon-orb" style={{ background: "#00F0FF", width: 260, height: 260, top: 40, right: -40, opacity: 0.3 }} />
      <div className="cyber-grid" />

      <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-300 mb-4 sc-mono"
          data-testid="ticket-back"
        >
          <ArrowLeft className="w-3 h-3" /> BACK TO SITE
        </Link>

        <div className="flex items-center gap-2 mb-2">
          <LifeBuoy className="w-4 h-4" style={{ color: "var(--sc-cyan)" }} />
          <span className="sc-label" style={{ color: "var(--sc-cyan)" }}>SUPPORT TICKET</span>
        </div>

        {loading && (
          <div className="term-card cyan p-8 text-center" data-testid="ticket-loading">
            <Loader2 className="w-6 h-6 mx-auto animate-spin" style={{ color: "var(--sc-cyan)" }} />
            <div className="sc-mono text-xs text-zinc-500 mt-3">Loading ticket...</div>
          </div>
        )}

        {!loading && err && (
          <div className="term-card p-8 text-center" data-testid="ticket-error">
            <Lock className="w-6 h-6 mx-auto text-zinc-500 mb-3" />
            <div className="sc-heading text-lg font-bold">{err}</div>
            <div className="text-sm text-zinc-500 mt-2">
              This link may have expired, or the token is invalid.
            </div>
            <Link to="/contact" className="sc-btn text-xs inline-flex items-center gap-1.5 mt-4">
              <MessageCircle className="w-3.5 h-3.5" /> Open a new ticket
            </Link>
          </div>
        )}

        {ticket && (
          <>
            {/* Header card */}
            <div className={`term-card ${STATUS_META[ticket.status]?.color || "cyan"} p-5 sm:p-6 mb-6`} data-testid="ticket-header">
              <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                <div className="min-w-0 flex-1">
                  <h1 className="sc-heading text-2xl sm:text-3xl font-black tracking-tight leading-snug">
                    {ticket.subject}
                  </h1>
                  <div className="sc-mono text-[11px] text-zinc-500 mt-2">
                    #{ticket.id.slice(0, 8).toUpperCase()} · {ticket.category_label} · Opened {new Date(ticket.created_at).toLocaleString()}
                  </div>
                </div>
                <span
                  className="chip-neon flex-shrink-0"
                  style={{
                    background: `${STATUS_META[ticket.status]?.dot || "#666"}22`,
                    borderColor: STATUS_META[ticket.status]?.dot || "#666",
                    color: STATUS_META[ticket.status]?.dot || "#ccc",
                  }}
                  data-testid="ticket-status-chip"
                >
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: STATUS_META[ticket.status]?.dot }} />
                  {STATUS_META[ticket.status]?.label || ticket.status}
                </span>
              </div>
              {ticket.status === "resolved" && (
                <div className="flex items-center gap-2 mt-3 pt-3 border-t border-white/10 text-xs text-zinc-400">
                  <CheckCircle2 className="w-4 h-4" style={{ color: "var(--sc-volt)" }} />
                  Marked resolved by staff. Reply below if you need more help — this will reopen the ticket.
                </div>
              )}
            </div>

            {/* Thread */}
            <div className="space-y-3 mb-6" data-testid="ticket-thread">
              {(ticket.messages || []).map((m) => (
                <MessageBubble key={m.id} m={m} />
              ))}
            </div>

            {/* Reply form (or closed banner) */}
            {ticket.status !== "closed" ? (
              <form onSubmit={sendReply} className="term-card cyan p-4" data-testid="ticket-reply-form">
                <div className="sc-label mb-2" style={{ color: "var(--sc-cyan)" }}>
                  ◈ ADD A REPLY
                </div>
                <textarea
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  required
                  minLength={1}
                  maxLength={4000}
                  rows={4}
                  data-testid="ticket-reply-input"
                  placeholder="Add more detail, attach a link, or ask a follow-up."
                  className="w-full px-3 py-2 bg-[#0F0F14] border border-white/10 focus:border-[color:var(--sc-cyan)] focus:outline-none rounded sc-mono text-xs text-zinc-100 placeholder:text-zinc-600 resize-none"
                />
                <div className="flex items-center justify-between mt-2">
                  <span className="text-[10px] text-zinc-600 sc-mono">{reply.length}/4000</span>
                  <button
                    type="submit"
                    disabled={sending || reply.trim().length < 1}
                    data-testid="ticket-reply-submit"
                    className="sc-btn text-xs flex items-center gap-1.5 disabled:opacity-60"
                  >
                    {sending ? (
                      <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Sending...</>
                    ) : (
                      <><Send className="w-3.5 h-3.5" /> Send Reply</>
                    )}
                  </button>
                </div>
              </form>
            ) : (
              <div className="term-card p-4 text-center text-xs text-zinc-400" data-testid="ticket-closed-banner">
                <Lock className="w-4 h-4 mx-auto mb-2 text-zinc-500" />
                This ticket is closed. Please <Link to="/contact" className="underline hover:text-white">open a new ticket</Link> if you have another question.
              </div>
            )}

            {/* Trust footer */}
            <div className="mt-8 pt-4 border-t border-white/5 flex items-center gap-2 text-[10px] sc-mono text-zinc-600">
              <Shield className="w-3 h-3" />
              This link is your ticket credential. Keep it private — anyone with the URL can view this thread.
            </div>
          </>
        )}
      </div>
    </div>
  );
}


function MessageBubble({ m }) {
  const isStaff = m.from === "staff";
  return (
    <div
      className={`p-4 rounded-lg border ${isStaff ? "ml-0 sm:ml-8" : "mr-0 sm:mr-8"}`}
      style={{
        background: isStaff ? "rgba(0,240,255,0.05)" : "rgba(255,255,255,0.03)",
        borderColor: isStaff ? "rgba(0,240,255,0.3)" : "rgba(255,255,255,0.08)",
      }}
      data-testid={`ticket-message-${m.from}`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span
            className="sc-mono text-[10px] px-2 py-0.5 rounded"
            style={{
              background: isStaff ? "var(--sc-cyan)" : "rgba(255,255,255,0.1)",
              color: isStaff ? "#0A0A0C" : "#ccc",
              fontWeight: 700,
            }}
          >
            {isStaff ? "STAFF" : "YOU"}
          </span>
          <span className="sc-heading text-xs font-bold text-zinc-300 truncate">
            {m.author}
          </span>
        </div>
        <span className="sc-mono text-[10px] text-zinc-600 flex items-center gap-1">
          <Clock className="w-3 h-3" /> {new Date(m.at).toLocaleString()}
        </span>
      </div>
      <div className="text-sm text-zinc-200 whitespace-pre-wrap leading-relaxed">
        {m.body}
      </div>
    </div>
  );
}
